import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography, Box, FormControl, InputLabel, Select, MenuItem, Badge } from '@material-ui/core';
import AnimatedFunnelBar from './skeletons/AnimatedFunnelBar';
import FunnelSkeleton from './skeletons/FunnelSkeleton';
import AnimatedInsightCard from './skeletons/AnimatedInsightCard';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import FilterListIcon from '@mui/icons-material/FilterList';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import { useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    funnelCard: {
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        padding: theme.spacing(3),
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
        marginTop: theme.spacing(3),
    },
    funnelTitle: {
        marginBottom: theme.spacing(3),
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    funnelContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(2, 0),
    },
    funnelStage: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    funnelBarContainer: {
        flex: 1,
        height: 60,
        position: 'relative',
    },
    funnelBar: {
        height: '100%',
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(90deg, #3b82f6, #6366f1)'
            : 'linear-gradient(90deg, #2196f3, #5e35b1)',
        borderRadius: 8,
        position: 'relative',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 2.5),
        justifyContent: 'space-between',
        cursor: 'pointer',
        '&:hover': {
            transform: 'scaleX(1.02)',
            boxShadow: theme.palette.type === 'dark'
                ? '0 4px 20px rgba(59, 130, 246, 0.4)'
                : '0 4px 20px rgba(33, 150, 243, 0.4)',
        },
    },
    funnelLabel: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
        color: '#fff',
    },
    funnelCount: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#fff',
    },
    funnelInfo: {
        minWidth: 200,
        textAlign: 'right',
    },
    funnelPercentage: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: theme.palette.type === 'dark' ? '#10b981' : '#4caf50',
    },
    funnelDropoff: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: theme.palette.type === 'dark' ? '#f87171' : '#e53935',
        marginTop: theme.spacing(0.5),
        lineHeight: 1.4,
    },
    criticalText: {
        fontWeight: 'bold',
    },
    insightsSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(3),
        background: theme.palette.type === 'dark'
            ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)',
        borderRadius: 16,
        border: `1px solid ${theme.palette.type === 'dark' ? '#334155' : '#e0e0e0'}`,
        boxShadow: theme.palette.type === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    insightsTitle: {
        fontSize: '2rem',
        fontWeight: 600,
        marginBottom: theme.spacing(3),
        color: theme.palette.text.primary,
    },
    headerSection: {
        display: 'flex',
        alignItems: "center",
        justifyContent: 'space-between',
    },
    insightsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: theme.spacing(2),
    },
    insightItem: {
        padding: theme.spacing(2.5),
        background: theme.palette.type === 'dark'
            ? 'rgba(30, 58, 95, 0.6)'
            : 'rgba(255, 255, 255, 0.7)',
        borderRadius: 12,
        border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`,
        borderLeft: `4px solid ${theme.palette.type === 'dark' ? '#3b82f6' : '#2196f3'}`,
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: theme.palette.type === 'dark'
                ? '0 6px 24px rgba(59, 130, 246, 0.3)'
                : '0 6px 24px rgba(33, 150, 243, 0.2)',
        },
    },
    insightLabel: {
        fontSize: '0.9rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    insightValue: {
        fontSize: '1.3rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        lineHeight: 1.3,
    },
    excellentValue: {
        color: theme.palette.type === 'dark' ? '#10b981' : '#4caf50',
    },
    goodValue: {
        color: theme.palette.type === 'dark' ? '#3b82f6' : '#2196f3',
    },
    poorValue: {
        color: theme.palette.type === 'dark' ? '#f59e0b' : '#ff9800',
    },
    criticalValue: {
        color: theme.palette.type === 'dark' ? '#ef4444' : '#f44336',
    },
    noDataText: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
    skeletonStage: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    datePickerContainer: {
        position: 'relative',
        zIndex: 9999
    },
    filterContainer: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        height: '50vh',
        overflow: 'auto'
    },
    buttonContainer: {
        width: "100%",
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary
    }
}));

function ConversionFunnel({ loading, data, compareDateRange, onCompareDateChange, onRefresh }) {
    const classes = useStyles();
    const theme = useTheme();
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [tempCompareDateRange, setTempCompareDateRange] = useState({
        compareStartDate: compareDateRange.compareStartDate,
        compareEndDate: compareDateRange.compareEndDate
    });

    // Calculate active filter count
    const getActiveFilterCount = () => {
        let count = 0;
        if (compareDateRange.compareStartDate && compareDateRange.compareEndDate) count++;
        return count;
    };

    const calculatedFilterCount = getActiveFilterCount();

    // Handle compare date change in dialog
    const handleCompareDateChange = ({ startDate, endDate }) => {
        setTempCompareDateRange({
            compareStartDate: startDate,
            compareEndDate: endDate
        });
    };

    // Apply filters
    const handleApplyFilters = () => {
        onCompareDateChange(tempCompareDateRange);
        setFilterDialogOpen(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        const defaultCompareDateRange = {
            compareStartDate: null,
            compareEndDate: null
        };
        setTempCompareDateRange(defaultCompareDateRange);
        onCompareDateChange(defaultCompareDateRange);
        setFilterDialogOpen(false);
    };

    // Cancel filter changes
    const handleCancelFilters = () => {
        setTempCompareDateRange({
            compareStartDate: compareDateRange.compareStartDate,
            compareEndDate: compareDateRange.compareEndDate
        });
        setFilterDialogOpen(false);
    };

    // Open filter dialog
    const handleOpenFilterDialog = () => {
        setTempCompareDateRange({
            compareStartDate: compareDateRange.compareStartDate,
            compareEndDate: compareDateRange.compareEndDate
        });
        setFilterDialogOpen(true);
    };

    // Filter dialog content
    const filterDialogContent = (
        <div className={classes.filterContainer}>
            <Typography className={classes.sectionTitle}>
                Comparison Date Range
            </Typography>
            <div className={classes.datePickerContainer}>
                <DateRangePickerOmni
                    startDate={tempCompareDateRange.compareStartDate}
                    endDate={tempCompareDateRange.compareEndDate}
                    onDateChange={handleCompareDateChange}
                    color="black"
                />
            </div>
        </div>
    );

    // Filter dialog actions
    const filterDialogActions = (
        <div className={classes.buttonContainer}>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleClearFilters} size="small" variant="outlined" label="Clear" />
            </div>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleCancelFilters} size="small" variant="outlined" label="Cancel" />
                <ButtonOmni onClick={handleApplyFilters} size="small" variant="outlined" label="Apply" />
            </div>
        </div>
    );

    if (loading) {
        return (
            <>
                <FunnelSkeleton classes={classes} />
            </>
        );
    }

    if (!data || !data.funnel || data.funnel.length === 0) {
        return (
            <Paper className={classes.funnelCard}>
                <Typography className={classes.noDataText}>
                    No funnel data available
                </Typography>
            </Paper>
        );
    }

    const { funnel, insights } = data;

    const formatNumber = (num) => {
        return num?.toLocaleString() || '0';
    };

    const getInsightValueClass = (insight) => {
        if (!insight) return classes.criticalValue;
        if (insight.includes('Excellent')) return classes.excellentValue;
        if (insight.includes('Good')) return classes.goodValue;
        if (insight.includes('Poor')) return classes.poorValue;
        return classes.criticalValue;
    };

    return (
        <>
            {insights && (
                <Box className={classes.insightsSection}>
                    <Box className={classes.headerSection}>
                        <Typography className={classes.insightsTitle}>
                            Key Insights
                        </Typography>
                        {calculatedFilterCount > 0 ? (
                            <Badge badgeContent={calculatedFilterCount} color="primary">
                                <ButtonOmni
                                    loading={false}
                                    onClick={handleOpenFilterDialog}
                                    size="small"
                                    variant="outlined"
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <FilterListIcon />
                                            Filters
                                        </Box>
                                    }
                                />
                            </Badge>
                        ) : (
                            <ButtonOmni
                                loading={false}
                                onClick={handleOpenFilterDialog}
                                size="small"
                                variant="outlined"
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <FilterListIcon />
                                        Filters
                                    </Box>
                                }
                            />
                        )}
                    </Box>
                    <div className={classes.insightsGrid}>
                        <AnimatedInsightCard
                            label="Delivery Health"
                            value={insights.delivery_health}
                            valueClass={classes.excellentValue}
                            classes={classes}
                            index={0}
                        />
                        <AnimatedInsightCard
                            label="Read Rate"
                            value={insights.read_rate}
                            valueClass={getInsightValueClass(insights.read_rate)}
                            classes={classes}
                            index={1}
                        />
                        <AnimatedInsightCard
                            label="Conversion Rate"
                            value={insights.conversion_rate}
                            valueClass={getInsightValueClass(insights.conversion_rate)}
                            classes={classes}
                            index={2}
                        />
                        <AnimatedInsightCard
                            label="Failed Messages"
                            value={`${formatNumber(insights.failed_messages)} (${insights.failed_percentage}%)`}
                            valueClass={classes.insightValue}
                            classes={classes}
                            index={3}
                        />
                        <AnimatedInsightCard
                            label="Biggest Drop-off"
                            value={insights.biggest_drop_off}
                            valueClass={classes.criticalValue}
                            classes={classes}
                            index={4}
                        />
                    </div>
                </Box>
            )}

            <Paper className={classes.funnelCard}>
                <Typography className={classes.funnelTitle}>
                    Conversion Funnel Analysis
                </Typography>

                <div className={classes.funnelContainer}>
                    {funnel.map((stage, index) => (
                        <AnimatedFunnelBar
                            key={stage.stage}
                            stage={stage}
                            index={index}
                            classes={classes}
                        />
                    ))}
                </div>
            </Paper>

            <ModalOmni
                open={filterDialogOpen}
                handleClose={() => setFilterDialogOpen(false)}
                title="Conversion Funnel Filters"
                content={filterDialogContent}
                actions={filterDialogActions}
                customWidth={800}
                disableScrollLock={true}
                style={{ overflow: 'visible' }}
                PaperProps={{
                    style: { overflow: 'visible' }
                }}
            />
        </>
    );
}

export default ConversionFunnel;